<template>
  <div>
    <div class="b-full-table__row b-full-table__row-inner">
      <div class="b-full-table__cell b-full-table__cell_type_flex">
        <button
          class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
          v-if="isContractRawEditing && !payment.isVirtual && (!isInClosedPeriod || currentContract.contract.status === 1)"
          @click="showEditPaymentModal()"
        >
          <dynamic-icon v-tooltip="$t('CONTENT.hints.editPayment')" class="b-btn__icon" icon="edit"></dynamic-icon>
        </button>
        <button
          class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
          v-if="isContractChangeConditions && !isContractEventState"
          @click="showEventRegistryModal()"
        >
          <dynamic-icon v-tooltip="$t('CONTENT.hints.changeConditionsPaymentHint')" class="b-btn__icon" icon="edit"></dynamic-icon>
        </button>
        <button
          class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
          v-if="lessorComponentShare.isEditForEvent && !payment.isVirtual"
          @click="showEventPaymentModal()"
        >
          <dynamic-icon v-tooltip="$t('CONTENT.hints.changeConditionsPaymentHint')" class="b-btn__icon" icon="edit"></dynamic-icon>
        </button>
        <div class="b-payment-row__info">
          <div class="b-payment-row__name">
            {{getPaymentName(payment.paymentNameId)}}
          </div>
          <div class="b-payment-row__info-about">
            <span class="b-payment-row__price-for-unit">{{formattedValue(payment.leaseRate)}} {{payment.currencyCode}}</span>
            за {{unitOfMeasure}} {{getPaymentFrequency(payment.leaseFrequency)}}
          </div>
          <div v-if="payment.externalId"
               class="b-contract__secondary-text mt-1">{{$t('CONTENT.assets.externalId')}}: {{payment.externalId}}</div>
          <div class="b-contract__secondary-text mt-1">{{getCurrencyRateDescription(payment)}}</div>
          <template v-if="payment.reclassSecurityPayments && payment.reclassSecurityPayments.length > 0">
            <div class="b-contract__secondary-text mt-1" v-for="reclass in payment.reclassSecurityPayments" :key="reclass.date">
              {{reclass.date | dateFormatted}}: {{reclass.value}} {{payment.currencyCode}} {{$t('CONTENT.payments.reclassed_security_payment_description')}}
            </div>
          </template>
        </div>
      </div>
      <div class="b-full-table__cell">{{payment.startDate | dateFormatted}} - {{payment.endDate | dateFormatted}}</div>
      <div class="b-full-table__cell">{{payment.firstPaymentDate | dateFormatted}}</div>
      <div class="b-full-table__cell">{{$t("CONTENT." + payment.paymentFrequency)}}</div>
      <div class="b-full-table__cell">
        {{getIndexationName(payment.indexation.indexationKindId)}}
        {{getPaymentIndexationPercent(payment.indexation.indexationPercent)}}
        <div v-if="payment.indexation.commentary"
             class="b-contract__secondary-text mt-1">{{$t('CONTENT.comment')}}: {{payment.indexation.commentary}}</div>
        <span v-if="payment.indexation.firstIndexationDate !== null">{{payment.indexation.firstIndexationDate | dateFormatted}}</span>
      </div>
      <div class="b-full-table__cell">
        {{ getDiscountRate(payment) }}
        <div v-if="payment.discount.commentary"
             class="b-contract__secondary-text mt-1">{{$t('CONTENT.comment')}}: {{payment.discount.commentary}}</div>
        <button
          v-if="lessorComponentShare.isNew && isContractEventState"
          type="button"
          class="b-btn b-btn_border_none b-btn--right"
          @click="removePayment">
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="delete"></dynamic-icon>
        </button>
      </div>
      <template v-if="isContractRawEditing && notVirtualPaymentsLength >= 2">
        <div class="b-full-table__cell b-full-table__cell_type_edit b-full-table_type_delete" v-if="!payment.isVirtual">
          <button v-if="!isInClosedPeriod" type="button" class="b-btn b-btn_border_none" @click="removePayment">
            <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="delete"></dynamic-icon>
          </button>
        </div>
        <div class="b-full-table__cell b-full-table__cell_type_edit"
             v-if="payment.isVirtual"></div>
      </template>
    </div>

    <popup :name="`contract-payment-${payment.idInDB}`" height="auto" :scrollable="true">
      <PaymentPopup
        :payment="payment"
        :unitOfMeasure="unitOfMeasure"
        :assetComponent="assetComponent"
        @close="closeEditPaymentPopup"/>
    </popup>
    <popup :name="`contract-payment-${payment.idInDB}-event`" height="auto" :scrollable="true">
      <PaymentEventPopup
        :payment="payment"
        :unitOfMeasure="unitOfMeasure"
        :assetComponent="assetComponent"
        @save="saveEventPaymentModal"
        @close="closeEventPaymentModal"/>
    </popup>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PaymentPopup from "./rawEditing/PaymentPopup";
import convertDecimalForChart from '../../../../utils/converters/convert.decimal.for.chart';
import PaymentEventPopup from "./rawEditing/PaymentEventPopup";

export default {
  components: {
    PaymentEventPopup,
    PaymentPopup,
  },
  props: {
    payment: {
      type: Object,
    },
    unitOfMeasure: {
      type: String,
    },
    assetComponent: {
      type: Object,
    },
    lessorComponentShare: {
      type: Object,
    }
  },
  computed: {
    ...mapGetters(['getFrequencies']),
    ...mapGetters(['getPaymentNames']),
    ...mapGetters(['getContractState']),
    ...mapGetters(['getCurrentContract']),
    ...mapGetters(['getContractConditionsState']),
    ...mapGetters(['contractIsInClosedPeriod']),
    ...mapGetters(['getContractEventState']),
    ...mapGetters(['getContractSettings']),
    allIndexations() {
      return this.$store.getters['indexationPlugin/getAllIndexations'];
    },
    isContractChangeConditions() {
      return this.getContractConditionsState;
    },
    currentContract() {
      return this.getCurrentContract;
    },
    isContractRawEditing() {
      return this.getContractState;
    },
    isInClosedPeriod() {
      return this.contractIsInClosedPeriod;
    },
    isContractEventState() {
      return this.getContractEventState;
    },
    notVirtualPaymentsLength() {
      const result = [];
      this.lessorComponentShare.paymentsParameters.forEach((payment) => {
        if (!payment.isVirtual) {
          result.push(payment);
        }
      });
      return result.length;
    },
  },
  methods: {
    getCurrencyCode() {
      return this.getContractSettings.currencyCode;
    },
    getCurrencyRateDescription(payment) {
      if (payment.currencyCode === this.getCurrencyCode()) return;
      const type = payment.currencyRateParameters.type;
      const params = payment.currencyRateParameters;

      switch (type) {
        case 'Fixed':
          return `${this.$t('CONTENT.currency.currencyRate_fix')}: ${params && params.fixedValue}`;

        case 'CBR':
          return this.$t('CONTENT.currency.currencyRate_CBR');

        case 'Corridor':
          return `${this.$t('CONTENT.currency.currencyRate_corridor')}: ${params && params.minValue} - ${params && params.maxValue}`;

        case 'Imported':
          return this.$t('CONTENT.currency.currencyRate_imported');

        case 'Corridor_Imported':
          return `${this.$t('CONTENT.currency.currencyRate_corridor_imported')}: ${params && params.minValue} - ${params && params.maxValue}`;

        default:
          return null;
      }
    },
    getIndexationName(indexationKind) {
      for (let i = 0; i < this.allIndexations.length; i++) {
        if(this.allIndexations[i].id === indexationKind) {
          return this.allIndexations[i].name;
        }
      }
    },
    getPaymentIndexationPercent(indexationPercent) {
      if(indexationPercent !== null) {
        return `${indexationPercent}%`;
      }
    },
    getPaymentName(paymentNameId) {
      for (let i = 0; i < this.getPaymentNames.length; i++) {
        if (this.getPaymentNames[i].idInDB === paymentNameId) {
          return this.getPaymentNames[i].name;
        }
      }
    },
    getPaymentFrequency(frequency) {
      let frequencyOrigin = frequency;
      let currentFrequency = `${this.$t('CONTENT.' + frequencyOrigin)}`;
      let [firstLetter, ...tail] = currentFrequency;
      let smallFrequency = firstLetter.toLowerCase() + tail.join('');

      if(frequency !== 'Once') {
        return `в ${smallFrequency}`;
      } else {
        return `${smallFrequency}`;
      }
    },
    getDiscountRate(payment) {
      const rate = parseFloat(payment.discount.discountRate);
      return payment.discount.isAuto
        ? `${rate}% ${this.$t('CONTENT.discountRates.auto')}`
        : `${rate}% ${this.$t('CONTENT.discountRates.manually')}`;
    },
    removePayment() {
      this.$eventBus.$emit('removePayment', this.payment);
    },
    showEventRegistryModal() {
      this.$eventRegistryManager.editPaymentParamsCommands(this.payment, this.assetComponent, this.lessorComponentShare);
    },
    showEventPaymentModal() {
      this.$modal.show(`contract-payment-${this.payment.idInDB}-event`);
    },
    saveEventPaymentModal(updatePayment) {
      this.payment.leaseRate = updatePayment.leaseRate;
    },
    closeEventPaymentModal() {
      this.$modal.hide(`contract-payment-${this.payment.idInDB}-event`);
    },
    showEditPaymentModal() {
      this.$modal.show(`contract-payment-${this.payment.idInDB}`);
    },
    closeEditPaymentPopup() {
      this.$modal.hide(`contract-payment-${this.payment.idInDB}`);
    },
    formattedValue(value) {
      return convertDecimalForChart(value)
    }
  }
};
</script>
